/**
 * The game's UI controller: owns the screen state, settings and language, renders the current screen into #ui over
 * the live canvas, and talks to the game only through UiHost.
 */
import { Lang, MessageKey, MessageParams, t } from '../i18n';
import { CoachLine } from '../game/coach';
import { h, icon } from './dom';
import { HowToUi } from './howto';
import { Hud } from './hud';
import { SCREEN_HELP, TIPS } from './keys';
import { LANG_TAG } from './logic/lang';
import type { BarMove } from './logic/loadPlan';
import { canGoBack, initialUi, reduce, ScreenId, UiEvent, UiState } from './logic/screens';
import { KeyValue, loadSettings, saveSettings, Settings } from './logic/settings';
import { nextCameraView } from '../view/logic/chase';
import { UiNavigator } from './input';
import { buildScreen } from './screens';
import { ClipUi } from './share';
import { Ride, RideResult, UiHost } from './types';
import './styles.css';

const storage = (): KeyValue | null => { try { return globalThis.localStorage ?? null; } catch { return null; } };

export class GameUi {
  state: UiState = initialUi();
  settings: Settings;
  ride: Ride | null = null;
  result: RideResult | null = null;
  replaying = false;
  private loadPctTimer: number | undefined;
  loading = { progress: 0, target: 0, stage: 'ui.loading.fonts' as MessageKey, error: null as null | 'gpu' | 'network', detail: '' };
  readonly root: HTMLElement;
  readonly hud: Hud;
  readonly nav: UiNavigator;
  readonly howto: HowToUi;
  readonly clip: ClipUi;
  host: UiHost | null = null;
  private screenEl: HTMLElement | null = null;
  private tipIndex = Math.floor(Math.random() * 4);

  constructor(public touch: boolean) {
    // Default language from the browser's primary language (uk -> UA, ru -> RUS, pl -> PL, else EN); a choice made in the
    // switch is saved with the settings and wins from then on.
    this.settings = loadSettings(storage(), [navigator.language]);
    const forced = new URLSearchParams(location.search).get('lang');
    if (forced === 'ua' || forced === 'pl' || forced === 'en' || forced === 'ru') this.settings.lang = forced;
    this.root = h('div.mx-ui', { attrs: { id: 'ui' } });
    document.body.append(this.root);
    document.body.classList.toggle('mx-touch-on', touch);
    this.hud = new Hud(this);
    this.nav = new UiNavigator(this);
    this.howto = new HowToUi(this);
    this.clip = new ClipUi(this);
    this.render();
    // The inline boot shell (index.html) has done its job: the real loading screen is up now.
    document.getElementById('boot')?.remove();
  }

  /** Switches the touch layout on after load (first touch, or the pointer turned coarse); never back off. */
  setTouch(on: boolean): void {
    if (!on || this.touch) return;
    this.touch = true;
    document.body.classList.add('mx-touch-on');
    this.render();
  }

  get lang(): Lang { return this.settings.lang; }
  tr = (key: MessageKey, params?: MessageParams): string => t(key, this.settings.lang, params);
  get tip(): MessageKey { return TIPS[this.tipIndex % TIPS.length]!; }

  dispatch(e: UiEvent): void {
    const before = this.state;
    this.state = reduce(this.state, e);
    if (this.state === before) return;
    if (before.screen !== this.state.screen) this.onScreenChange(before.screen, this.state.screen);
    this.render();
  }

  go(to: ScreenId): void { this.dispatch({ type: 'go', to }); }
  back(): void {
    if (this.state.help) { this.dispatch({ type: 'help', open: false }); return; }
    if (this.howto.back()) return;
    if (this.state.screen === 'play') { this.dispatch({ type: 'pause' }); return; }
    if (this.state.screen === 'pause') { this.dispatch({ type: 'resume' }); return; }
    if (this.replaying) { this.host?.stopReplay(); return; }
    if (canGoBack(this.state)) this.dispatch({ type: 'back' });
  }

  /** Next riding camera (chase -> helmet cam -> chase), saved with the settings. */
  toggleCamera(): void {
    this.updateSettings({ camera: nextCameraView(this.settings.camera) });
    // The HUD camera button names the view it switches to; relabel it in place (the play screen is not re-rendered).
    const label = document.querySelector('.camera-btn .cam-label');
    if (label) label.textContent = this.tr(this.settings.camera === 'fpv' ? 'hud.cam.chase' : 'hud.cam.fpv');
  }

  updateSettings(patch: Partial<Settings>): void {
    const langChanged = patch.lang !== undefined && patch.lang !== this.settings.lang;
    this.settings = { ...this.settings, ...patch };
    saveSettings(storage(), this.settings);
    this.host?.applySettings(this.settings);
    if (langChanged) { this.render(); this.hud.relabel(); this.clip.relabel(); }
  }

  // ---- called by the game ----
  /**
   * Moves the loading bar (src/ui/logic/loadPlan.ts BarMove): one CSS transform transition toward `to` over `ms`, run
   * by the compositor, so the bar keeps moving while the main thread is busy. Never goes back.
   */
  setLoading(m: BarMove): void {
    const to = Math.max(this.loading.target, m.to);
    this.loading.target = to;
    this.loading.progress = Math.max(this.loading.progress, m.from);
    this.loading.stage = m.stage;
    const bar = this.root.querySelector<HTMLElement>('.load-bar i');
    const label = this.root.querySelector<HTMLElement>('.load-stage');
    if (bar) {
      // Long creeps ease out (fast start, slow approach); a finished step catches up quickly.
      bar.style.transition = m.ms > 0 ? `transform ${Math.round(m.ms * 1.6)}ms cubic-bezier(.15, .6, .35, 1)` : 'transform 300ms ease-out';
      bar.style.transform = `scaleX(${to.toFixed(4)})`;
    }
    if (label) label.textContent = this.tr(m.stage);
    this.syncLoadPct();
    this.loadPctTimer ??= window.setInterval(() => this.syncLoadPct(), 200);
    if (to >= 1) { clearInterval(this.loadPctTimer); this.loadPctTimer = undefined; this.loading.progress = 1; this.syncLoadPct(); }
  }

  /** The percentage label follows the bar's current (transitioning) scale; a few times a second, never per frame. */
  private syncLoadPct(): void {
    const bar = this.root.querySelector<HTMLElement>('.load-bar i');
    if (bar) {
      const t = getComputedStyle(bar).transform; // matrix(a, b, c, d, e, f): a = scaleX
      const a = t.startsWith('matrix(') ? Number(t.slice(7).split(',')[0]) : NaN;
      if (Number.isFinite(a)) this.loading.progress = Math.max(this.loading.progress, Math.min(a, this.loading.target));
    }
    const p = Math.round(this.loading.progress * 100);
    const pct = this.root.querySelector<HTMLElement>('.load-pct');
    if (pct) pct.textContent = `${p}%`;
    this.root.querySelector('.load-bar')?.setAttribute('aria-valuenow', String(p));
  }

  loadFailed(kind: 'gpu' | 'network' = 'gpu', detail = ''): void {
    clearInterval(this.loadPctTimer); this.loadPctTimer = undefined;
    this.loading.error = kind; this.loading.detail = detail; this.render();
  }

  loaded(to?: ScreenId, ride?: Ride): void {
    if (ride) this.ride = ride;
    this.host?.applySettings(this.settings);
    this.dispatch({ type: 'loaded', to });
  }

  /** A ride ended by itself (drill done, trial finished, route finished). */
  finish(result: RideResult): void {
    this.result = result;
    this.dispatch({ type: 'finish' });
  }

  coach(line: CoachLine): void { this.hud.coach(this.tr(line.key, line.params)); }
  toast(text: string, kind: 'info' | 'good' | 'bad' = 'info'): void { this.hud.toast(text, kind); }

  replayState(on: boolean): void {
    this.replaying = on;
    this.render();
  }

  // ---- internals ----
  startRide(r: Ride): void {
    this.ride = r;
    this.result = null;
    if (this.state.screen === 'intro') { this.host?.startRide(r); this.dispatch({ type: 'go', to: 'play' }); return; }
    this.go('intro');
  }

  private onScreenChange(from: ScreenId, to: ScreenId): void {
    const host = this.host;
    if (!host) return;
    if (to === 'pause') host.pause(true);
    if (from === 'pause' && to === 'play') host.pause(false);
    if (to === 'intro' && (from === 'pause' || from === 'results')) host.quitRide();
    if ((to === 'start' || to === 'levels') && (from === 'pause' || from === 'results')) host.quitRide();
    if (to !== 'results' && this.replaying) host.stopReplay();
    host.setCamera(to === 'play' ? 'ride' : to === 'garage' ? 'garage' : 'menu');
    if (to === 'start') this.tipIndex++;
  }

  render(): void {
    document.documentElement.lang = LANG_TAG[this.lang];
    const s = this.state;
    document.body.dataset.screen = s.screen;
    document.body.classList.toggle('mx-playing', s.screen === 'play' || this.replaying);
    document.body.classList.toggle('mx-replaying', this.replaying);
    const demo = this.howto?.phase === 'demo';
    document.body.classList.toggle('mx-howto-demo', demo);
    // While a how-to demo plays the screen under it (pause, results) steps aside: the view is the demo.
    const el = demo ? h('section.screen.howto-screen') : buildScreen(this, s.screen);
    const prev = this.screenEl;
    const sameScreen = prev?.dataset.screen === s.screen;
    el.dataset.screen = s.screen;
    if (sameScreen) el.classList.add('no-anim');
    this.root.replaceChildren(el);
    this.screenEl = el;
    if (s.screen !== 'loading') this.root.append(this.helpButton());
    const howto = this.howto?.render();
    if (howto) this.root.append(howto);
    if (s.help) this.root.append(this.helpOverlay());
    this.hud.setVisible(s.screen === 'play' || this.replaying);
    this.nav.focusFirst(sameScreen);
  }

  private helpButton(): HTMLElement {
    return h('button.help-fab', {
      attrs: { 'aria-label': this.tr('ui.help'), title: this.tr('ui.help'), 'data-nav': '' },
      on: { click: () => this.dispatch({ type: 'help', open: true }) },
    }, [icon('help')]);
  }

  private helpOverlay(): HTMLElement {
    const close = () => this.dispatch({ type: 'help', open: false });
    return h('div.modal', { attrs: { role: 'dialog', 'aria-modal': 'true' }, on: { click: (e) => { if (e.target === e.currentTarget) close(); } } }, [
      h('div.panel.glass.help-card', {}, [
        h('div.eyebrow', {}, [icon('info'), h('span', { text: this.tr('help.title') })]),
        h('p.lead', { text: this.tr(SCREEN_HELP[this.state.screen]) }),
        h('p.muted', { text: this.tr('help.nav') }),
        // Riding screens: what the controls do (and that Pro controls live in Settings).
        ['play', 'intro', 'pause'].includes(this.state.screen) ? h('p', { text: this.tr(this.settings.beginner ? 'help.mode.simplified' : 'help.mode.real') }) : null,
        h('button.btn.primary', { attrs: { 'data-nav': '', autofocus: true }, on: { click: close } }, [h('span', { text: this.tr('ui.close') })]),
      ]),
    ]);
  }
}
